import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  CreateAmendmentDialogViewModel,
  type AmendmentVisibilityOption,
} from './CreateAmendmentDialogViewModel'
import { FormColumn, FormColumns, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import {
  CustomSelect,
  type RenderItemProps,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { CreateAmendmentDialog } from '@/features/jobs/details/components/AmendmentsCard/CreateAmendmentDialog'

export interface CreateAmendmentDialogContainerProps {
  vm: CreateAmendmentDialogViewModel
}

export const CreateAmendmentDialogContainer = observer(
  function CreateAmendmentDialogContainer({
    vm,
  }: CreateAmendmentDialogContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static(
          CreateAmendmentDialogViewModel.VISIBILITY_OPTIONS,
          (item, text) => {
            return item.visibility.toLowerCase().includes(text.toLowerCase())
          },
        ),
      [],
    )
    const selectProps = useCustomSelectStateFromItemSource({ itemSource })

    const descriptionError = vm.validation.getError('description')
    const internalNoteError = vm.validation.getError('internalNote')
    const visibilityError = vm.validation.getError('visibility')

    return (
      <CreateAmendmentDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        onSubmit={vm.createAmendment}
        isSubmitPending={vm.createAmendment.pending}
        isSubmitRejected={vm.createAmendment.rejected}
        submitError={vm.createAmendment.error}
        onSubmitErrorClose={vm.createAmendment.reset}
      >
        <FormRow>
          <TextInputContainer
            model={vm}
            label="Description"
            field="description"
            name="description"
            placeholder="E.g. the client wanted additional services, or requested a refund"
            error={descriptionError}
            status={descriptionError ? 'danger' : 'default'}
            maxLength={CreateAmendmentDialogViewModel.MAX_DESCRIPTION_LENGTH}
            data-test="amendment-description-input"
          />
        </FormRow>
        <FormRow>
          <TextInputContainer
            model={vm}
            label="Internal note"
            field="internalNote"
            name="internal note"
            help="For our own internal reference"
            error={internalNoteError}
            status={internalNoteError ? 'danger' : 'default'}
            maxLength={CreateAmendmentDialogViewModel.MAX_INTERNAL_NOTE_LENGTH}
            data-test="amendment-internal-note-input"
          />
        </FormRow>
        <FormRow>
          <FormGroupContainer label="Visibility" error={visibilityError}>
            <CustomSelect
              {...selectProps}
              multi={false}
              value={vm.option}
              placeholder="Select"
              onChange={vm.setVisibilityOption}
              itemToString={(i) => i.displayName}
              renderItem={(itemProps) => (
                <SelectedVisibility itemProps={itemProps} />
              )}
              icon="dropdown"
              iconColor="var(--grey600)"
              status={visibilityError ? 'danger' : 'default'}
              testId="amendment-visibility-dropdown"
            />
          </FormGroupContainer>
        </FormRow>
        <FormRow>
          <FormColumns>
            <FormColumn>
              <NumericInputContainer
                model={vm}
                precision={2}
                label="Client amount"
                field="clientAmount"
                testId="add-amendment-modal-client-amount-input"
                help="If positive, client pays more. If negative, client is refunded"
              />
            </FormColumn>
            <FormColumn>
              <NumericInputContainer
                model={vm}
                precision={2}
                label="Pro amount"
                field="providerAmount"
                testId="add-amendment-modal-pro-amount-input"
                help="If positive, pro gets more. If negative, pro is penalised"
              />
            </FormColumn>
          </FormColumns>
        </FormRow>
      </CreateAmendmentDialog>
    )
  },
)

interface SelectedVisibilityProps {
  itemProps: RenderItemProps<AmendmentVisibilityOption>
}

function SelectedVisibility({ itemProps }: SelectedVisibilityProps) {
  return <SingleItemSelectedDefault {...itemProps} />
}
